import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, isValid, parseISO } from 'date-fns';
import { ResponseError } from 'api-client';
import type { SignupSummary } from 'api-client';
import { useAppContext } from '@/context';
import { MUIBarChart, MUIAreaChart } from '../analytics';
import { buildSignupSeries } from '../../signupSeries';

const ProjectDashboard = () => {
  const { apiClients } = useAppContext();
  const [params, setParams] = useSearchParams();
  const start = params.get('start');
  const end = params.get('end');
  const [startDate, setStartDate] = useState<Date | null>(start === null ? null : parseISO(start));
  const [endDate, setEndDate] = useState<Date | null>(end === null ? null : parseISO(end));
  const [summary, setSummary] = useState<SignupSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState<number>(0);
  const validDates = startDate && endDate && isValid(startDate) && isValid(endDate);
  const reversed = validDates && startDate > endDate;
  const rows = useMemo(() => (summary ? buildSignupSeries(summary) : []), [summary]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    setSummary(null);
    apiClients.dashboard
      .dashboardSignupSummaryRetrieve({ start: start ?? undefined, end: end ?? undefined })
      .then((result) => {
        if (!active) return;
        setSummary(result);
        // Server date-only values are UTC instants; the picker needs local calendar dates.
        setStartDate(parseISO(result.start.toISOString().slice(0, 10)));
        setEndDate(parseISO(result.end.toISOString().slice(0, 10)));
      })
      .catch((failure) => {
        if (!active) return;
        setError(
          failure instanceof ResponseError && failure.response.status === 400
            ? 'Choose valid start and end dates.'
            : failure instanceof ResponseError && [401, 403].includes(failure.response.status)
              ? 'Project analytics requires staff access.'
              : 'Could not load analytics. Please try again.',
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [apiClients.dashboard, start, end, reload]);

  const updateRange = () => {
    if (!validDates || reversed) return;
    const next = new URLSearchParams(params);
    next.set('start', format(startDate, 'yyyy-MM-dd'));
    next.set('end', format(endDate, 'yyyy-MM-dd'));
    if (next.toString() === params.toString()) setReload((value) => value + 1);
    else setParams(next);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Stack spacing={3} sx={{ py: { xs: 2, sm: 3 }, minWidth: 0 }}>
        <Typography component="h1" variant="h4">
          Project Dashboard
        </Typography>
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="stretch">
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={setStartDate}
              disabled={loading}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={setEndDate}
              disabled={loading}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  error: Boolean(reversed),
                  helperText: reversed ? 'End date must be on or after start date.' : undefined,
                },
              }}
            />
            <Button
              variant="contained"
              loading={loading}
              disabled={loading || !validDates || Boolean(reversed)}
              onClick={updateRange}
              sx={{ flexShrink: 0 }}
            >
              Update range
            </Button>
          </Stack>
        </Paper>
        {error && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" onClick={() => setReload((value) => value + 1)}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}
        {summary && (
          <Typography color="text.secondary">
            Showing {summary.start.toISOString().slice(0, 10)} through {summary.end.toISOString().slice(0, 10)}. Users
            registered before this range: {summary.startValue}.
          </Typography>
        )}
        {(loading || summary) && (
          <>
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <MUIBarChart
                  title="User Signups (Daily)"
                  loading={loading}
                  data={rows.map((row) => ({ name: row.date, value: row.count }))}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <MUIAreaChart
                  title="Registered Users (Total)"
                  loading={loading}
                  data={rows.map((row) => ({ date: row.date, value: row.cumulative }))}
                />
              </Box>
            </Stack>
            {!loading && (
              <TableContainer
                component={Paper}
                tabIndex={0}
                role="region"
                aria-label="Signup statistics"
                sx={{
                  maxHeight: 360,
                  overflow: 'auto',
                  '&:focus-visible': {
                    outline: (theme) => `2px solid ${theme.palette.readable.link}`,
                    outlineOffset: 2,
                  },
                }}
              >
                <Table size="small" stickyHeader sx={{ minWidth: 360 }}>
                  <caption>Signup statistics</caption>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Daily signups</TableCell>
                      <TableCell>Registered users</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.date}>
                        <TableCell>{row.date}</TableCell>
                        <TableCell>{row.count}</TableCell>
                        <TableCell>{row.cumulative}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Stack>
    </LocalizationProvider>
  );
};

export default ProjectDashboard;
